/* eslint-disable max-lines-per-function, max-lines */
import { EventBus, Events } from "jiffy-store";
import { productItemsPluralizer } from "common/product-items-pluralizer";
import { sendDataLayer, sendDataLayerOnLinkClickEvent } from "jiffy-analytics";
import { trackProductAddedModal } from "components/jiffy/product-details-quantity-form/analytics";
import postponeRendering from "common/utils/web-vitals";
import { api } from "jiffy-api";
import { initApp } from "components/jiffy/jiffy-services-preview/jiffy-services-preview";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import idleCallback from "common/utils/ui/idle_callback";
import { PRICES } from "components/jiffy/vue-product-details-print/misc/constants";
import selectors from "components/jiffy/product-details-delivery-date-simple/selectors";
import { isDeliveryNonEstimatable } from "components/jiffy/product-details-delivery-date-simple/utils";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { FreeShippingProgressController } from "components/jiffy/free-shipping-progress/free-shipping-progress";
import { isFastDeliveryEstimateEnabled } from "common/utils/delivery-date/fetch-pdp-delivery-estimates";
import legacyApi from "common/utils/api";
import initSavedCartsName from "./continue-shopping-modal-saved-carts-name/continue-shopping-modal-saved-carts-name";
import "./continue-shopping-modal-awards/continue-shopping-modal-awards";
import "./continue-shopping-modal-title/continue-shopping-modal-title";
import "./continue-shopping-modal-footer/continue-shopping-modal-footer";
import "./continue-shopping-modal.scss";
import "../blanks-promotion-banner/blanks-promotion-banner";

const progressBarController = new FreeShippingProgressController("#continue-shopping-modal");

const applyShippingCostInfo = info => {
  progressBarController.hideSpinner();
  progressBarController.updateProgressBar({
    total: info.free_shipping_threshold - info.amount_until_free,
    threshold: info.free_shipping_threshold,
    showFreeShippingWithAccount: false
  });
};

const fetchAndApplyCartShippingInfo = async () => {
  if (!isFastDeliveryEstimateEnabled()) return;

  const $container = document.getElementById("product-page-container");
  const productId = $container?.dataset?.productId;
  const compositionName =
    $container?.dataset?.selectedColor || document.getElementById("active-color-name")?.value;
  if (!productId || !compositionName) return;

  try {
    const response = await legacyApi.product.fetchCartShippingInfo({
      product_id: productId,
      composition_name: compositionName
    });

    if (response?.success && response?.shipping_cost_info) {
      applyShippingCostInfo(response.shipping_cost_info);
    } else {
      progressBarController.hideSpinner();
    }
  } catch {
    progressBarController.hideSpinner();
  }
};

const serializer = new XMLSerializer();
const preCutSelectedClass = ".js-continue-shopping-modal-pre-cut-selected";
const preCutPriceClass = ".js-continue-shopping-modal-pre-cut-amount";
const catalogQuantityClass = ".js-continue-shopping-modal-quantity";
const bannersWrapperSelector = ".js-continue-shopping-services-preview";
const bannersSelector = "#jiffy-services-preview-atc";
const defaultBannerSelector = ".js-continue-shopping-modal-default-banner";
const dynamicPromoSelector = ".js-continue-shopping-modal-dynamic-promo";
const dynamicPromoWinnerClassPrefix = "continue-shopping-modal__dynamic-promo--winner-";

const $$getModal = () => $("#continue-shopping-modal");
const $getItemsRow = () => document.querySelector(".js-continue-shopping-modal-items-row");
const $getItemsRowCatalogNumber = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal-catalog-number");
const $getMadeInlineIcon = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal-made-inline-icon");
const $getItemsRowCatalogSize = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal-size");
const $getItemsRowCatalogQuantity = $itemsRow => $itemsRow.querySelector(catalogQuantityClass);
const $getItemsRowCatalogPrice = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal-amount");
const $getItemsRowCatalogPreCutSelected = $itemsRow => $itemsRow.querySelector(preCutSelectedClass);
const $getItemsRowCatalogPreCutPrice = $itemsRow => $itemsRow.querySelector(preCutPriceClass);
const $getItemsRowNotice = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal__items-notice");
const $getJiffyServicesPreview = () =>
  document.querySelector(`${bannersSelector} > jiffy-services-preview`);
const $getAwardsSection = () => document.querySelector(".continue-shopping-modal-awards");
const $getColorItem = $itemsRow =>
  $itemsRow.querySelector(".js-continue-shopping-modal-color-item");
const $getBogoAtcModal = () => document.getElementById("bogo-atc-modal");

let trackingAdditionalProps = {};
const trackAnalytics = () => {
  const modal = $$getModal();
  const viewCartButton = $(".js-button-view-cart", modal);
  const continueShoppingButton = $(".js-button-continue-shopping", modal);
  const selectMoreButton = $(".js-button-select-more", modal);

  selectMoreButton.on("click", () => {
    postponeRendering(() => {
      modal.modal("hide");
    });

    postponeRendering(() => {
      EventBus.publish(Events.PRODUCT_STATIC_COLOR_SELECTED);
      sendDataLayer({
        genericEvent: true,
        eventCategory: "PDP",
        event: "Add to cart modal button click",
        eventLabel: "Select More Colors",
        eventValue: 1
      });
      trackProductAddedModal("select_more_color", trackingAdditionalProps);
    });
  });

  viewCartButton.on("click", event => {
    const link = event.target;
    if (link) {
      sendDataLayerOnLinkClickEvent(
        event,
        {
          genericEvent: true,
          eventCategory: "PDP",
          event: "Add to cart modal button click",
          eventLabel: "View Cart",
          eventValue: 1
        },
        link
      );
      trackProductAddedModal("view_cart", {
        ...trackingAdditionalProps,
        modal: "original_atc"
      });
    }
  });

  continueShoppingButton.on("click", e => {
    e.preventDefault();

    postponeRendering(() => {
      modal.modal("hide");
    });
    submitDtfEvent("dtf_continue_shopping_modal_click", {
      action: "continue_shopping",
      business_line: trackingAdditionalProps.business_line,
      free_shipping_threshold: trackingAdditionalProps.free_shipping_threshold,
      modal: "original_atc"
    });
    sendDataLayer({
      genericEvent: true,
      eventCategory: "PDP",
      event: "Add to cart modal button click",
      eventLabel: "Continue Shopping",
      eventValue: 1
    });
    trackProductAddedModal("continue_shopping", trackingAdditionalProps);
  });

  modal.on("hide.bs.modal", () => {
    sendDataLayer({
      genericEvent: true,
      eventCategory: "PDP",
      event: "Add to cart modal closed",
      eventLabel: null,
      eventValue: 1
    });

    const suggestionContainer = document.querySelector(".js-suggestion-container");
    if (suggestionContainer) {
      suggestionContainer.innerHTML = "";
    }

    const shippingModal = document.querySelector(".js-continue-shopping-modal");
    if (shippingModal) {
      shippingModal.classList.remove("continue-shopping-modal--full-size");
    }
  });
};

const initWithDynamicData = () => {
  if (document.pdpDynamicData.signed_in) {
    initSavedCartsName();
  }
};

const waitForDynamicData = async () => {
  if (window.dynamicDataLoaded) {
    initWithDynamicData();
  } else {
    document.addEventListener("dynamicDataLoaded", initWithDynamicData, false);
  }
};

const getPriceMatch = price => {
  const priceMatch = price.match(/([^\d]*)([\d,]*\.\d{2})/);
  const fallback = ["$0", "$", 0];
  return priceMatch || fallback;
};

const getPriceTotal = item => {
  const [, symbol, priceNumber] = getPriceMatch(item.priceTotal ?? item.price);
  let priceTotal = priceNumber;

  if (!item.priceTotal) {
    const { madePrice } = item;
    priceTotal = ((Number(priceNumber) + (madePrice || 0)) * item.quantity).toFixed(2);
  }

  if (item.isEnhancement) {
    priceTotal = Number(priceNumber).toFixed(2);
  }

  return { symbol, priceTotal };
};

const getItemTemplate = item => {
  const { symbol, priceTotal } = getPriceTotal(item);
  const $itemsRow = $getItemsRow();
  const $itemsRowTemplate = document.importNode($itemsRow, true);

  if (document.querySelector(".js-dtf-add-to-cart-modal") || item.isMade) {
    $getItemsRowCatalogNumber($itemsRowTemplate).textContent = `${item.catalogNumber}`;
  } else {
    $getItemsRowCatalogNumber($itemsRowTemplate).textContent =
      `${item.brandName} ${item.catalogNumber}`;
  }

  $getMadeInlineIcon($itemsRowTemplate).classList.toggle("hidden", !item.made);
  $getItemsRowCatalogSize($itemsRowTemplate).textContent = item.size;
  $getItemsRowCatalogPrice($itemsRowTemplate).textContent = `${symbol}${priceTotal}`;

  if (item.preCut) {
    const $preCutSelectedElement = $getItemsRowCatalogPreCutSelected($itemsRowTemplate);
    $preCutSelectedElement.textContent = "Precut - Yes";
    $preCutSelectedElement.classList.add("hidden");

    const $preCutPriceElement = $getItemsRowCatalogPreCutPrice($itemsRowTemplate);
    $preCutPriceElement.textContent = `${symbol}${parseFloat(item.preCutPrice).toFixed(2)} each`;
    $preCutPriceElement.classList.add("hidden");
  }

  const quantityText = productItemsPluralizer(item.quantity, item.soldInCase ? "case" : "item");

  $getItemsRowCatalogQuantity($itemsRowTemplate).textContent = quantityText;

  const $colorItem = $getColorItem($itemsRowTemplate);

  if ($colorItem) {
    $colorItem.textContent = item.color;
  }

  $itemsRowTemplate.classList.remove("hidden");
  return serializer.serializeToString($itemsRowTemplate);
};

const initAddToCartUpsell = async () => {
  const { catalogNumber, skipUpsell } = $$getModal().data();

  if (!catalogNumber || skipUpsell) return;

  const $upsellBlock = $getJiffyServicesPreview();
  const data = await api.jiffyshirts.cart.getCartUpsell({
    catalog_number: catalogNumber
  });

  const { upsellServices, showCrossSell, showMadeSell } = objNamesToCamelCase(data);

  if (showMadeSell) {
    $upsellBlock?.setAttribute(":services", JSON.stringify(upsellServices));
    $upsellBlock?.setAttribute("header-text", "Need us to press your design on a shirt?");
    $upsellBlock?.setAttribute("mixpanel-event", "made-on-transfers-atc-upsell");
    $upsellBlock?.setAttribute("design", "");
    initApp(bannersWrapperSelector);
  } else if (showCrossSell) {
    const services = Object.keys(upsellServices);
    const onlyTransfers = services.length === 1 && services.includes("DTF001");
    const hasStickers = services.includes("STKRDC001");
    const designType = onlyTransfers || hasStickers ? "" : "large";

    if (designType) {
      $upsellBlock?.setAttribute("header-text", "Need your design on a T-Shirt or hoodie?");
    }
    $upsellBlock?.setAttribute(":services", JSON.stringify(upsellServices));
    $upsellBlock?.setAttribute("mixpanel-event", "cross_sell_atc_click");
    $upsellBlock?.setAttribute("design", designType);

    initApp(bannersWrapperSelector);

    $getAwardsSection()?.classList.toggle("hidden");
  } else {
    $upsellBlock?.classList.add("hidden");
  }
};

const switchDeliveryDateMode = mode => {
  const $defaultShipments = document.querySelector(selectors.global.MODAL_SHIPMENTS_LIST);
  const $madeShipments = document.querySelector(selectors.made.MODAL_SHIPMENTS_LIST);
  $defaultShipments?.classList.toggle("hidden", mode === "made");
  $madeShipments?.classList.toggle("hidden", mode !== "made");

  const $defaultDateCutOff = document.querySelector(selectors.global.MODAL_DATES_CUTOFF);
  const $madeDateCutOff = document.querySelector(selectors.made.MODAL_DATES_CUTOFF);
  $defaultDateCutOff?.classList.toggle("hidden", mode === "made");
  $madeDateCutOff?.classList.toggle("hidden", mode !== "made");
};

const showPromotionalBanner = () => {
  const $modal = document.getElementById("continue-shopping-modal");
  const $bannerWrapper = $modal?.querySelector(".js-continue-shopping-services-preview");

  if (!$modal || !$bannerWrapper) return;

  // Check for custom banner first (from API response), then fall back to cached banner
  const $$modal = $$getModal();
  const customBannerHtml = $$modal.data("custom-banner-html");
  const bannerHtml = customBannerHtml || $modal.dataset.bannerHtml;

  if (bannerHtml) {
    $bannerWrapper.innerHTML = bannerHtml;
    $bannerWrapper.classList.remove("hidden");
  }
};

const removeWinnerModifierClasses = $el => {
  if (!$el) return;
  Array.from($el.classList)
    .filter(klass => klass.startsWith(dynamicPromoWinnerClassPrefix))
    .forEach(klass => $el.classList.remove(klass));
};

const promoBannerVisibleModalClass = "continue-shopping-modal--promo-banner-visible";

const applyPromoBannerByWinner = winner => {
  const $modal = document.getElementById("continue-shopping-modal");
  if (!$modal) return;

  const $defaultBanner = $modal.querySelector(defaultBannerSelector);
  const $dynamicPromo = $modal.querySelector(dynamicPromoSelector);

  if (!$defaultBanner || !$dynamicPromo) return;

  const isNone = !winner || winner === "none";

  removeWinnerModifierClasses($dynamicPromo);

  if (isNone) {
    $dynamicPromo.classList.add("hidden");
    $defaultBanner.classList.remove("hidden");
    $modal.classList.remove(promoBannerVisibleModalClass);
  } else {
    $defaultBanner.classList.add("hidden");
    $dynamicPromo.classList.remove("hidden");
    $dynamicPromo.classList.add(`${dynamicPromoWinnerClassPrefix}${winner}`);
    $modal.classList.add(promoBannerVisibleModalClass);

    const $title = $modal?.querySelector(".continue-shopping-modal-title");
    const $viewCartButton = $modal?.querySelector(".js-button-view-cart");
    if ($title) $title.classList.add("continue-shopping-modal-title--centered");
    if ($viewCartButton) $viewCartButton.dataset.promoBanner = "true";
  }
};

export default async () => {
  const upsell = document.querySelector(".js-suggestion-container");
  const title = document.querySelector(".js-continue-shopping-modal-title");

  waitForDynamicData();

  trackAnalytics();
  await initAddToCartUpsell();

  EventBus.on(Events.MOBILE_GRID_SUBMIT, ({ target }) => {
    const $$modal = $$getModal();
    if ($$modal.length === 0) return;

    const $$modalItems = $$modal.find(".continue-shopping-modal__items");
    $$modalItems.empty();

    const { dtfData, form, madeParams, bannerHtml } = target;
    const items = {};

    // Clear previous custom banner and update if new one provided
    if (bannerHtml) {
      $$modal.data("custom-banner-html", bannerHtml);
      $$modal.data("skip-upsell", true);
    } else {
      $$modal.data("custom-banner-html", null);
      $$modal.data("skip-upsell", false);
    }

    $$modal[0]
      .querySelector(".js-button-view-cart")
      .classList.add("continue-shopping-modal-footer__button--disabled");

    if (dtfData) {
      dtfData.forEach((artwork, index) => {
        items[index] || (items[index] = []);
        items[index].push(artwork);
      });
    } else {
      const thumbnail =
        $(".js-carousel-thumbnails img:first").attr("data-src") ||
        $(".js-carousel-thumbnails img:first").attr("src") ||
        $(".js-buy-it-again-modal-picture").attr("src");

      const productTitle = $(".js-product-details-title").text().trim();
      const productBrandImageSrc = $(".js-product-details-subtitle-brand").attr("src");

      let madePrice = 0;

      if (madeParams?.isMade) {
        const chargePayload = madeParams.madePayload.filter(item => !item.isEnhancement);
        madePrice = chargePayload.length === 2 ? PRICES.MADE_DEDICATED_FRONT_AND_BACK : 0;
      }

      $(form)
        .find("input[type=tel]")
        // eslint-disable-next-line func-names
        .each(function () {
          const quantity = $(this).val();
          if (quantity) {
            const $input = $(this);
            const price = $input
              .closest(".js-product-details-quantity-form-row-item")
              .find(".js-product-details-quantity-form-row-amount")
              .children("span")
              .text();
            const colorFeaturedSrc = $input
              .closest(".js-form-loading-placeholder")
              .data("color-feature-src");
            // Use attr() so size reflects current DOM; .data() caches and can show stale size after
            // the user changes size (e.g. in the size selector) and the grid is re-rendered.
            const size = $input.attr("data-size") ?? $input.data("size");

            items[colorFeaturedSrc] || (items[colorFeaturedSrc] = []);
            items[colorFeaturedSrc].push({
              catalogNumber: $input.attr("data-catalog-number") ?? $input.data("catalog-number"),
              color: $input.attr("data-color") ?? $input.data("color"),
              image: $input.attr("data-image") || $input.data("image") || thumbnail,
              size,
              j1Badge: $input.attr("data-j1badge") === "true" || $input.data("j1badge") === true,
              made: madeParams.isMade,
              quantity: parseInt(quantity, 10),
              soldInCase:
                $input.attr("data-variant-sold-in-case") === "true" ||
                $input.data("variant-sold-in-case"),
              brandName: $input.attr("data-brand-name") ?? $input.data("brand-name"),
              price,
              productTitle,
              productBrandImageSrc,
              madePrice
            });
          }
        });

      const isSample = form?.elements?.sample;

      if (isSample?.value === "true") {
        items.samples = [];
        const sampleInputs = form.querySelectorAll(".js-sample-qty-input");
        sampleInputs.forEach($input => {
          const brandName = $input.getAttribute("data-brand-name") ?? $input.dataset.brandName;
          const catalogNumber =
            $input.getAttribute("data-catalog-number") ?? $input.dataset.catalogNumber;
          const size = $input.getAttribute("data-size") ?? $input.dataset.size;
          const color = document.getElementById("active-color-name").value;
          if ($input.value) {
            items.samples.push({
              quantity: $input.value,
              price: "",
              image: thumbnail,
              made: madeParams.isMade,
              madePrice: 0.99,
              color,
              brandName,
              catalogNumber,
              size,
              productTitle,
              productBrandImageSrc
            });
          }
        });
      }
    }

    if (madeParams?.isMade) {
      madeParams.madePayload.forEach(madeItem => {
        if (!madeItem.isEnhancement) {
          return;
        }

        items[madeItem.catalogNumber] ??= [];
        items[madeItem.catalogNumber].push({
          isMade: true,
          ...madeItem
        });
      });

      switchDeliveryDateMode("made");
    }

    $.each(items, (_, colorItems) => {
      const template = colorItems.map(item => getItemTemplate(item)).join("");
      $$modalItems.append(template);
    });

    if (target.madeParams?.isEdit) return;

    idleCallback(async () => {
      // Clear banner wrapper content before re-initializing
      const $bannerWrapper = document.querySelector(".js-continue-shopping-services-preview");
      if ($bannerWrapper) {
        $bannerWrapper.innerHTML = "";
      }

      await initAddToCartUpsell();

      if (upsell) {
        title.innerHTML = "You've added the following items";
      }

      showPromotionalBanner();

      // focus view cart button
      const viewCartButton = $(".js-button-view-cart", $$modal);
      viewCartButton.focus();
    });
  });

  EventBus.on(Events.ADD_TO_CART, () => {
    progressBarController.showSpinner();
  });

  EventBus.on(Events.ITEMS_ADDED_TO_CART, ({ target }) => {
    const {
      free_shipping_threshold: shippingThreshold,
      sub_order_total: totalString,
      show_free_shipping_with_account: showFreeShippingWithAccount,
      pre_cut: preCut,
      replace_images: replaceImages,
      artwork_urls: artworkUrls,
      is_dtf: isDtf,
      is_made: isMade,
      success,
      notices,
      bogo_promotion_message: bogoPromotionMessage
    } = target;
    const $$modal = $$getModal();
    const { businessLine, catalogNumber } = $$modal.data();

    trackingAdditionalProps = {
      free_shipping_threshold: shippingThreshold,
      business_line: businessLine,
      catalog_number: catalogNumber
    };

    const winner = target.promotion_resolution?.winner;

    // Check if BOGO promotion applies and show v2 modal, otherwise show v1
    if (winner === "bogo") {
      if (isDtf && $getBogoAtcModal()) {
        EventBus.publish(Events.SHOW_BOGO_ATC_MODAL, target);
        return;
      }
      if (!isDtf) {
        EventBus.publish(Events.SHOW_ADDED_TO_CART_MODAL_V2, {
          bogoMessage: bogoPromotionMessage,
          maxFreeDimensionLabel: target.max_free_dimension_label,
          trackingProps: trackingAdditionalProps
        });
        return;
      }
      // Fall through to v1 modal for DTF when bogo-atc-modal doesn't exist
    }

    isMade && switchDeliveryDateMode("made");

    if (progressBarController.$progressContent) {
      if (!isFastDeliveryEstimateEnabled()) progressBarController.hideSpinner();

      if (success) {
        if (preCut) {
          const $preCutSelectedElements = document.querySelectorAll(preCutSelectedClass);
          const $preCutPriceElements = document.querySelectorAll(preCutPriceClass);
          const $catalogQuantityElements = document.querySelectorAll(catalogQuantityClass);

          $preCutSelectedElements.forEach($preCutSelectedElement => {
            $preCutSelectedElement.classList.remove("hidden");
          });
          $preCutPriceElements.forEach($preCutPriceElement => {
            $preCutPriceElement.classList.remove("hidden");
          });
          $catalogQuantityElements.forEach($catalogQuantityElement => {
            $catalogQuantityElement.textContent += ",";
          });
        }

        if (notices) {
          const $noticesContainer = document.querySelector(
            ".js-continue-shopping-modal__notices-container"
          );
          const $noticesList = document.querySelector(".js-continue-shopping-modal__notices-list");
          const failedArtworkAttachments = notices.failed_artwork_attachments;
          const $rowItems = document.querySelectorAll(".js-continue-shopping-modal-items-row");

          if (failedArtworkAttachments) {
            failedArtworkAttachments.forEach(artwork => {
              const $listItem = document.createElement("li");
              $listItem.classList.add("continue-shopping-modal__notices-list-item");
              $listItem.textContent = artwork.filename;
              $noticesList.appendChild($listItem);

              const $itemRow = $rowItems[artwork.index];
              $getItemsRowNotice($itemRow).classList.remove("hidden");
            });
            $noticesContainer.classList.remove("hidden");
          }
        }

        if (replaceImages) {
          const $modalImages = document.querySelectorAll(".js-continue-shopping-modal-image");
          $modalImages.forEach(($modalImage, index) => {
            // Only replace those with existing src
            if ($modalImage.src) $modalImage.src = artworkUrls[index];
          });
        }

        if (isFastDeliveryEstimateEnabled()) {
          fetchAndApplyCartShippingInfo();
        } else {
          progressBarController.updateProgressBar({
            total: totalString,
            threshold: shippingThreshold,
            showFreeShippingWithAccount
          });
        }

        $$modal[0]
          .querySelector(".js-button-view-cart")
          .classList.remove("continue-shopping-modal-footer__button--disabled");
      }
    }

    // Show the v1 modal only if BOGO doesn't apply
    $$modal.modal("show");

    if (upsell) {
      title.innerHTML = "You've added the following items";
    }

    applyPromoBannerByWinner(winner);
    showPromotionalBanner();

    // focus view cart button
    const viewCartButton = $(".js-button-view-cart", $$modal);
    viewCartButton.focus();
  });

  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (_evt, { _color, showEstimates }) => {
    const deliveryDates = document.querySelector(".js-continue-shopping-delivery-dates");
    deliveryDates?.classList?.toggle("hidden", isDeliveryNonEstimatable(showEstimates));
  });

  window.jiffy?.madeStore?.watch?.(
    state => state.app.mode,
    mode => switchDeliveryDateMode(mode),
    { immediate: true }
  );
};
